import { CSSProperties, useState, useEffect, Fragment, useRef } from "react";
import useCampaignBuilder, { transformPostsToCreativeFormat } from "Hooks/useCampaignBuilder";
import { clsx } from "modules/Utils";
import Sty from "modules/Sty";
import Colors from "modules/Colors";
import ButtonSymphony from "elements/ButtonSymphony";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";
import useTikTokConnections from "Hooks/useTikTokConnections";
import { toast } from 'react-toastify';
import { AdConnectionOptions, InstagramPost, MediaProductType, MediaType, TikTokSparkAd } from "modules/Types";
import CardSymphony from "elements/CardSymphony";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import TabSymphony from "elements/TabSymphony";
import Icon from "elements/Icon";
import { Select } from "@mui/material";
import { MenuItem } from "@mui/material";
import SwitchSymphony from "elements/SwitchSymphony";
import { Caption } from "components/shareable/Typography";
import { Body2 } from "components/shareable/Typography";
import useFacebookConnections from "Hooks/useFacebookConnections";
import ItemSelector, { ItemSelectorAction } from "elements/ItemSelector";
import { CreativeSelectorPost } from "Hooks/useCampaignBuilder";
import CloudinaryUploadWidget, { CloudinaryUploadedFileType } from "./CloudinaryUploadWidget";
import ModalSymphony from "elements/ModalSymphony";
import { loadInstagramPosts, searchInstagramPosts_api } from "api/facebookApi";
import useBrand, { BrandExistsState } from "Hooks/useBrand";
import { MAESTRO_STATUS, MaestroLoadingMessage } from "pages/MarketingPage/MaestroBanner";
import Banner from "elements/Banner";
import { getVideoFromUrl_api, ImportFromURLResponse } from "api/agentApi";
import { importMultipleInstagramPosts_api } from "api/campaignApi";
import { toastError } from "modules/Toast";
import DebugBox from "components/shareable/DebugBox";
import Intercom from "helpers/Intercom";
import { PlaybookType, PlanType } from "modules/Const";
import UpgradeToUnlockCTA from "pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA";
import { FeatureKeys } from 'features/definitions';
import { useFeature } from 'Hooks/useFeature';
import FeatureGate from 'components/FeatureGate';

/** Maximum number of posts that can be selected (now dynamic based on subscription) */

export enum AssetPlatformType {
    INSTAGRAM = 'Instagram',
    TIKTOK = 'TikTok',
    FACEBOOK = 'Facebook',
    CLOUDINARY = 'Uploaded Asset',
    IMPORTED_FROM_URL = 'Imported from URL',
}

interface AdCreativeSelectorCampaignProps {
    className: string;
    style?: CSSProperties;
    variant?: 'tiktok' | 'facebook';
    allowedUploadTypes?: ('posts' | 'upload' | 'sparkCode' | 'recentlyUsed' | 'url-search')[];
    hideTitle?: boolean;
    hideSubtitle?: boolean;
}

const AdCreativeSelectorCampaign = ({
    className,
    style,
    allowedUploadTypes = ['posts', 'upload', 'sparkCode', 'recentlyUsed', 'url-search'],
    variant = 'tiktok',
    hideTitle = false,
    hideSubtitle = false,
}: AdCreativeSelectorCampaignProps) => {
    const { currentBrand } = useBrand() as BrandExistsState;

    // Use feature gate to determine A/B testing availability
    const { isAvailable: canABTest } = useFeature(FeatureKeys.CAMPAIGN_AB_TESTING);
    // If user has A/B testing access, MAX_POSTS is null (unlimited). Otherwise, it's 1.
    const MAX_POSTS = canABTest ? null : 1;

    const {
        setSelectedInstagramPosts,
        selectedInstagramPosts,
        selectedSparkCodes,
        removeSelectedInstagramPost,
        uploadedAssets,
        setUploadedAssets,
        getUploadedAssets,
        removeUploadedAsset,
        importedFromUrlPosts,
        setImportedFromUrlPosts,
        removeImportedFromUrlPost,
        removeSparkCode,
        addSparkCode,
        setPreviewedAsset,
        previewedAsset,
        usesExpressSetup,
        getAllSelectedPosts,
        playbookType
    } = useCampaignBuilder();

    const {
        selectedInstagramPage,
        loggedInFacebookUser,
        selectedFacebookAdAccount,
        loginWithFacebook,
        selectedFacebookPage,
        isLoggingIntoFacebook,
        renderFacebookLoginButton,
        showConnectionsModal,
        setShowConnectionsModal,
    } = useFacebookConnections();

    const requireFBLogin = playbookType === PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT
    const hasFinishedLogin = loggedInFacebookUser && selectedFacebookAdAccount && selectedFacebookPage

    let useExpressSetupFlow = usesExpressSetup();
    const canUsePlatformPosts = !useExpressSetupFlow && selectedInstagramPage && variant !== 'tiktok'

    const getCreativeTabs = (variant: 'tiktok' | 'facebook', isExpressSetup: boolean) => {
        const tabs: TabItem[] = []

        switch (variant) {
            case 'tiktok':
                if (allowedUploadTypes.includes('sparkCode')) {
                    tabs.push({ id: 'sparkCode', title: 'Spark Code' })
                }
                break;
            case 'facebook':
                // Always show Instagram Posts tab for Facebook variant
                if (allowedUploadTypes.includes('posts')) {
                    tabs.push({ id: 'posts', title: 'Instagram Posts' })
                }

                // Always show Upload Assets for Facebook variant
                if (allowedUploadTypes.includes('upload')) {
                    tabs.push({ id: 'upload', title: 'Upload Assets' })
                }

                if (allowedUploadTypes.includes('url-search')) {
                    tabs.push({ id: 'url-search', title: 'Import from URL' })
                }
                break;
        }

        // Make sure we have at least one tab available
        if (tabs.length === 0) {
            if (variant === 'facebook' && allowedUploadTypes.includes('upload')) {
                tabs.push({ id: 'upload', title: 'Upload Assets' })
            } else if (variant === 'tiktok' && allowedUploadTypes.includes('sparkCode')) {
                tabs.push({ id: 'sparkCode', title: 'Spark Code' })
            }
        }

        return tabs
    }

    let creativeTabs = getCreativeTabs(variant, useExpressSetupFlow);

    const { validateSparkCode, isValidatingSparkCode } = useTikTokConnections();

    const [sparkCode, setSparkCode] = useState<string>('');
    const [creativeTab, setCreativeTab] = useState<TabItem>(creativeTabs[0]);

    // Facebook-specific state
    const [isLoadingInstagramPosts, setIsLoadingInstagramPosts] = useState<boolean>(false);
    const [loadedInstagramPosts, setLoadedInstagramPosts] = useState<InstagramPost[]>([]);
    const [isViewOnlyRecommended, setIsViewOnlyRecommended] = useState<boolean>(false);
    const [selectedTimeFilter, setSelectedTimeFilter] = useState<string>('');
    // For pagination
    const [hasMorePosts, setHasMorePosts] = useState<boolean>(true);
    const [isLoadingMorePosts, setIsLoadingMorePosts] = useState<boolean>(false);
    const [currentOffset, setCurrentOffset] = useState<number>(0);
    // For cursor-based pagination
    const [nextCursor, setNextCursor] = useState<string | null>(null);

    // For Searching a post via URL
    const [showSearchSelectorModal, setShowSearchSelectorModal] = useState<boolean>(false);
    const [searchPostsInput, setSearchPostsInput] = useState<string>('');
    const [isLoadingSearchPosts, setIsLoadingSearchPosts] = useState<boolean>(false);
    const [searchPostsResults, setSearchPostsResults] = useState<InstagramPost[]>([]);

    // For importing a post from a URL
    const DEV_TESTING_DEFAULT_URL = 'https://instagram.com/p/DI4YwjMRKne/'
    const IS_DEV = process.env.NODE_ENV === 'development'
    const [importFromUrlInput, setImportFromUrlInput] = useState<string>(IS_DEV ? DEV_TESTING_DEFAULT_URL : '');
    const [isImportingFromUrl, setIsImportingFromUrl] = useState<boolean>(false); // NOTE: this is a loading state

    // For expanding/collapsing selected posts list
    const [isSelectedPostsExpanded, setIsSelectedPostsExpanded] = useState<boolean>(false);
    const [localImportedFromUrlPost, setLocalImportedFromUrlPost] = useState<ImportFromURLResponse | null>(null);

    // For importing multiple posts at once
    const [showMultipleImportModal, setShowMultipleImportModal] = useState<boolean>(false);
    const [multipleUrlsInput, setMultipleUrlsInput] = useState<string>('');
    const [isImportingMultipleUrls, setIsImportingMultipleUrls] = useState<boolean>(false);

    // for displaying the posts
    // const [transformedPosts, setTransformedPosts] = useState<CreativeSelectorPost[]>([]);
    // const [selectedPosts, setSelectedPosts] = useState<CreativeSelectorPost[]>([]);

    const selectedPosts = getAllSelectedPosts(variant);

    const buttonRef = useRef<HTMLButtonElement>(null);

    /** Handle successful upload from Cloudinary */
    const handleAssetUpload = (info: CloudinaryUploadedFileType) => {
        const uploadedAssetsState = getUploadedAssets();
        // Add your upload handling logic here
        setUploadedAssets([...uploadedAssetsState, info]);
        // set the previewed asset on upload
        const transformedPost = transformPostsToCreativeFormat([info])[0]
        setPreviewedAsset(transformedPost)
    };

    /** 
     * Shared function to fetch and process Instagram posts
     * @param isInitialLoad - Whether this is the initial load or loading more posts
     */
    const fetchAndProcessInstagramPosts = async (isInitialLoad: boolean = false) => {
        // For initial load, set loading state and reset pagination variables
        if (isInitialLoad) {
            setIsLoadingInstagramPosts(true);
            setHasMorePosts(true);
            setCurrentOffset(0);
            setNextCursor(null);
        } else {
            // For "load more", check if we're already loading or if there are no more posts
            if (isLoadingMorePosts) return;

            if (!selectedInstagramPage || !hasMorePosts || !nextCursor) {
                return;
            }

            setIsLoadingMorePosts(true);
        }

        try {
            // Make API call with appropriate parameters
            const response = await loadInstagramPosts({
                instagramPage: selectedInstagramPage,
                limit: 50,
                after: isInitialLoad ? undefined : (nextCursor || undefined)
            });

            // If loading more and no new posts, we've reached the end
            if (!isInitialLoad && response.posts.length === 0) {
                setHasMorePosts(false);
                return;
            }

            // Sort posts by timestamp (most recent first)
            const sortedPosts = [...response.posts].sort((a, b) => {
                const dateA = new Date(a.timestamp || 0).getTime();
                const dateB = new Date(b.timestamp || 0).getTime();
                return dateB - dateA; // Most recent first
            });

            // Update state based on whether this is initial load or loading more
            if (isInitialLoad) {
                setLoadedInstagramPosts(sortedPosts);
                setCurrentOffset(sortedPosts.length);
            } else {
                // Add new posts to existing posts
                setLoadedInstagramPosts(prevPosts => [...prevPosts, ...sortedPosts]);
                setCurrentOffset(prev => prev + sortedPosts.length);
            }

            // Update pagination state
            setNextCursor(response.paging.cursors.after);
            setHasMorePosts(Boolean(response.paging.cursors.after));
        } catch (error) {
            toast.error(`Failed to ${isInitialLoad ? 'load' : 'load more'} Instagram posts`);
            console.error(error);
            setHasMorePosts(false);
        } finally {
            // Reset loading state based on operation type
            if (isInitialLoad) {
                setIsLoadingInstagramPosts(false);
            } else {
                setIsLoadingMorePosts(false);
            }
        }
    };

    /** Load Instagram posts when component mounts or when selectedInstaPage changes */
    useEffect(() => {
        if (variant === 'facebook' && selectedInstagramPage) {
            fetchAndProcessInstagramPosts(true);
        }
    }, [variant, selectedInstagramPage]);

    /** Load more Instagram posts when the "Load More" button is clicked */
    const loadMorePosts = async () => {
        if (!selectedInstagramPage || !hasMorePosts) return;
        await fetchAndProcessInstagramPosts(false);
    };

    useEffect(() => {
        if (!importFromUrlInput || importFromUrlInput === '') {
            setLocalImportedFromUrlPost(null)
        }
    }, [importFromUrlInput])

    useEffect(() => {
        let allowedCreativeTabs = getCreativeTabs(variant, useExpressSetupFlow);
        setCreativeTab(allowedCreativeTabs[0])
    }, [useExpressSetupFlow])

    /** 
     * Syncs selectedPosts state with previously selected Instagram posts from draft campaign
     * Runs when transformedPosts are loaded and selectedInstagramPosts exists from draft
     */
    // useEffect(() => {
    //     if (transformedPosts.length > 0 && selectedInstagramPosts.length > 0) {
    //         const previouslySelectedPosts = transformedPosts.filter(post =>
    //             selectedInstagramPosts.some(selectedPost => selectedPost.id === post.id)
    //         );
    //         setSelectedPosts(previouslySelectedPosts);

    //         // Set the first selected post as the previewed asset
    //         if (previouslySelectedPosts.length > 0) {
    //             setPreviewedAsset(previouslySelectedPosts[0]);
    //         }
    //     }
    // }, [transformedPosts, selectedInstagramPosts]);

    /** Handles the validation and addition of a new spark code */
    const handleValidateSparkCode = async () => {
        if (!sparkCode) return;

        try {
            const response = await validateSparkCode({ sparkCode, isUsingExpressSetup: true });

            // Add the validated spark code to the list
            // addSparkCode returns false if the spark code is a duplicate
            const wasAdded: boolean = addSparkCode(response);

            if (wasAdded) {
                toast.success('Spark code validated successfully');
            } else {
                toast.success('This Spark Code has already been added');
            }

            // Clear the input
            setSparkCode('');

        } catch (error) {
            toast.error('Failed to validate spark code');
        }
    }

    const handlePreviewSparkCode = (post: any) => {
        console.log(post);
    }

    const renderSparkTab = () => {
        return (
            <TabSymphony
                className="creativeTabAdSelector"
                visible={creativeTab.id === 'sparkCode'}
                style={sty.creativeTabAdSelector}
            >
                <div className="creativeTabAdSelectorTitle" style={Sty.text16grey}>
                    Enter a TikTok Spark Code to run ads using your post directly.
                </div>

                <ButtonSymphony
                    className="learnMoreSparkCodeButton"
                    onClick={() => Intercom.openTikTokSparkAdsGuide()}
                    variant="text"
                >
                    Learn how to find your Spark Code →
                </ButtonSymphony>

                <InputSymphony
                    className="sparkCodeInput"
                    value={sparkCode}
                    onChange={(value) => setSparkCode(value as string)}
                    placeholder="Enter your Spark Code here..."
                />

                <ButtonSymphony
                    className="createSparkCodeButton"
                    disabled={isValidatingSparkCode || !sparkCode || (MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS)}
                    isLoading={isValidatingSparkCode}
                    variant="primary"

                    onClick={handleValidateSparkCode}
                    loadingText="Validating Spark Code..."
                >
                    Validate Spark Code
                </ButtonSymphony>
            </TabSymphony>
        )
    }

    const renderRecentlyUsedTab = () => {
        return (
            <TabSymphony
                className="recentlyUsedTabPanel"
                style={sty.recentlyUsedTabPanel}
                visible={creativeTab.id === 'recentlyUsed'}
            >
                {/* {renderSelectedSparkCodes()} */}

            </TabSymphony>
        )
    }
    /** Handles removing a post based on its type */
    const handleRemovePost = (post: CreativeSelectorPost) => {
        switch (post.originalPostType) {
            case AssetPlatformType.INSTAGRAM:
                removeSelectedInstagramPost(post.id);
                break;
            case AssetPlatformType.CLOUDINARY:
                removeUploadedAsset(post.id);
                break;
            case AssetPlatformType.TIKTOK:
                removeSparkCode(post.id);
                break;
            case AssetPlatformType.IMPORTED_FROM_URL:
                removeImportedFromUrlPost(post.id);
                break;
        }

    }

    /** Handles previewing a post */
    const handlePreviewPost = (post: CreativeSelectorPost) => {
        // Always set the previewed asset regardless of post type
        setPreviewedAsset(post);
    }

    const handleCloseSearchPostsModal = () => {
        setShowSearchSelectorModal(false)
        setSearchPostsResults([])
        setIsLoadingSearchPosts(false)
    }

    const handleSearchPosts = async () => {
        try {
            setSearchPostsResults([])
            setIsLoadingSearchPosts(true);
            const response = await searchInstagramPosts_api({
                brandId: currentBrand?.id,
                query: searchPostsInput
            })

            if (response) {
                const igPostsToTransform: InstagramPost[] = response.map((post) => ({
                    ...post,
                    platformType: AssetPlatformType.INSTAGRAM
                }))

                setSearchPostsResults(igPostsToTransform)
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingSearchPosts(false);
        }
    }

    const renderSelectedPostRow = (post: CreativeSelectorPost, index: number) => {
        let titleText = `Ad ${index + 1}`
        let postType = post.originalPostType
        let isPreviewing = post.id === previewedAsset?.id

        return (
            <div
                key={post.id}
                className="selectedSparkPostContainer"
                style={sty.selectedSparkPostContainer}
            >
                <img
                    className="selectedPostImage"
                    src={post.thumbnailUrl}
                    alt={post.title}
                    style={sty.selectedSparkPostImage}
                />
                <div className="selectedPostTextContainer" style={{ marginRight: 'auto' }}>
                    <div className="selectedPostTitle" style={Sty.text16bold}>{titleText}</div>
                    <div className="selectedPostTitle" style={Sty.text14}>{postType}</div>
                </div>
                <ButtonSymphony
                    className="previewPostButton1"
                    onClick={() => handlePreviewPost(post)}
                    variant="text"
                    disabled={isPreviewing}
                >
                    {isPreviewing ? 'Previewing' : 'Preview'}
                </ButtonSymphony>
                <Icon
                    className="removePostTrashButton"
                    style={{ flexShrink: 0 }}
                    icon="trash"
                    color={Colors.greyDark}
                    onClick={() => handleRemovePost(post)}
                />
            </div>
        )
    }

    const renderSelectedPosts = () => {
        let title;
        if (selectedPosts.length > 0) {
            title = MAX_POSTS === null ? `Selected Posts (${selectedPosts.length})` : `Selected Post (${selectedPosts.length} / ${MAX_POSTS})`;
        } else {
            title = MAX_POSTS === 1 ? 'Select 1 post for your ad' : 'Select unlimited posts to test for your ad';
        }

        if (creativeTab.id === 'posts') {
            if (!hasFinishedLogin || useExpressSetupFlow) {
                return null;
            }
        }

        return (
            <div className="selectedPostsContainer" style={sty.selectedPostsContainer}>
                <FeatureGate
                    feature={FeatureKeys.CAMPAIGN_AB_TESTING}
                    upgradeBannerTitle="Upgrade to Growth to A/B test unlimited posts."
                    upgradeBannerSubtitle=""
                    showBanner={true}
                    upgradeSource="ad-creative-selector-ab-testing"
                    buttonText="Upgrade to Growth"
                >
                    <Banner
                        className="adCreativeSelectorBanner"
                        color="maestro"
                        title={title}
                        iconLeft={
                            <Icon
                                icon="maestroBadgeImage"
                                className="maestroBannerIcon"
                                size={42}
                            />
                        }
                    >
                        You can select as many posts as you want to A/B test, and your highest performing ad(s) will receive most of the budget.
                    </Banner>
                </FeatureGate>
                {selectedPosts.length > 0 && (
                    <>
                        <ButtonSymphony
                            className="expandCollapseButton"
                            onClick={() => setIsSelectedPostsExpanded(!isSelectedPostsExpanded)}
                            variant="text"
                            iconLeft={<Icon icon={isSelectedPostsExpanded ? "chevronUp" : "chevronDown"} size={16} className="expandIcon" />}
                            style={{ alignSelf: 'flex-start', marginTop: 8 }}
                        >
                            {isSelectedPostsExpanded ? 'Hide selected posts' : 'View selected posts'}
                        </ButtonSymphony>

                        {isSelectedPostsExpanded && (
                            <div className="selectedPostsList" style={sty.selectedPostsList}>
                                {selectedPosts.map((post, index: number) => (
                                    renderSelectedPostRow(post, index)
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    /** 
     * Handles selecting/deselecting a post based on its type
     * Updates the appropriate state in useCampaign hook
     */
    const handleSelectPost = (post: CreativeSelectorPost) => {
        // Check if we're at the maximum selection limit
        console.log('selected post', post)

        const isSelected = selectedPosts.some(p => p.id === post.id);

        if (!isSelected && MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS) {
            return;
        }

        // Set this as the previewed asset on selection
        if (!isSelected) {
            setPreviewedAsset(post);
        }

        // Update the appropriate state in useCampaign hook based on post type
        switch (post.originalPostType) {
            case AssetPlatformType.INSTAGRAM: {
                const isSelectedInIG = selectedInstagramPosts.some(p => p.id === post.id);

                if (isSelectedInIG) {
                    removeSelectedInstagramPost(post.id);
                    break;
                }

                // Try to find post in loaded posts first
                const originalPost = loadedInstagramPosts.find(p => p.id === post.id);
                if (originalPost) {
                    setSelectedInstagramPosts([...selectedInstagramPosts, originalPost]);
                    break;
                }

                // If not found in loaded posts, check search results
                const foundSearchedPost = searchPostsResults.find(p => p.id === post.id);
                if (foundSearchedPost) {
                    setSelectedInstagramPosts([...selectedInstagramPosts, foundSearchedPost]);
                }

                break;
            }
            case AssetPlatformType.CLOUDINARY: {
                const isSelectedInUploads = uploadedAssets.some(p => p.id === post.id);
                if (isSelectedInUploads) {
                    removeUploadedAsset(post.id);
                } else {
                    const originalAsset = uploadedAssets.find(p => p.id === post.id);
                    if (originalAsset) {
                        setUploadedAssets([...uploadedAssets, originalAsset]);
                    }
                }
                break;
            }
            case AssetPlatformType.TIKTOK: {
                // Check if this post is already selected in the TikTok spark codes
                const isSelectedInSpark = selectedSparkCodes.some(p => {
                    // Match by item_id or video_id or our generated ID
                    const sparkId = p.item_info?.item_id || p.video_info?.video_id;
                    return sparkId === post.id;
                });

                if (isSelectedInSpark) {
                    // Remove if already selected
                    removeSparkCode(post.id);
                } else {
                    // Check if we're at the maximum selection limit
                    if (MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS) {
                        return;
                    }

                    // The post might be in the selectedSparkPosts array as part of the mock data
                    const mockSparkPost = selectedSparkCodes.find(p => p.item_info?.item_id === post.id);

                    // if (mockSparkPost) {
                    //     // Create a TikTokSparkAd-like object from the mock data
                    //     const sparkAdObject = {
                    //         platformType: AssetPlatformType.TIKTOK,
                    //         item_info: {
                    //             item_id: mockSparkPost.itemId,
                    //             text: mockSparkPost.text,
                    //             // Add required properties for TikTokItemInfo
                    //             anchor_list: null,
                    //             stitch_original_item_id: '',
                    //             auth_code: '',
                    //             duet_original_item_id: '',
                    //             is_multi_duet_stitch: false,
                    //             mentioned_item_ids: null
                    //         },
                    //         user_info: {
                    //             identity_type: 'tiktok',
                    //             identity_id: mockSparkPost.itemId,
                    //             identity_display_name: mockSparkPost.text
                    //         },
                    //         auth_info: {
                    //             auth_start_time: new Date().toISOString(),
                    //             auth_end_time: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                    //         }
                    //     } as TikTokSparkAd;

                    //     addSparkCode(sparkAdObject);
                    // } else {
                    //     console.warn('Could not find original TikTok post for selection:', post.id);
                    // }
                }
                break;
            }
            case AssetPlatformType.IMPORTED_FROM_URL: {
                const isSelectedInImportedFromUrl = importedFromUrlPosts.some(p => p.id === post.id);
                if (isSelectedInImportedFromUrl) {
                    removeImportedFromUrlPost(post.id);
                } else {
                    if (localImportedFromUrlPost) {
                        setImportedFromUrlPosts([...importedFromUrlPosts, localImportedFromUrlPost]);
                    }
                }
                break;
            }
        }
    }

    const renderViewOnlyRecommended = () => {
        return (
            <SwitchSymphony
                className="viewOnlyRecommendedSwitch"
                style={{ marginTop: 16 }}
                checked={isViewOnlyRecommended}
                onChange={() => setIsViewOnlyRecommended(!isViewOnlyRecommended)}
                label="View only recommended by * AI Posts"
                labelPlacement="right"
            />
        )
    }

    const renderTimeSelectFilter = () => {
        return (
            <InputSymphony
                className="timeFilterSelect"
                value={selectedTimeFilter}
                onOptionSelect={(option) => setSelectedTimeFilter(option.value as string)}
                placeholder="Select time filter"
                options={[
                    {
                        value: 'recents',
                        title: 'Recents',
                        description: 'View recently created content',
                    },
                    {
                        value: 'futures',
                        title: 'Futures',
                        description: 'View upcoming scheduled content',
                    }
                ] as SelectOptionItem[]}
                isSelectDropdown
            />
        )
        return (
            <Select
                className={clsx('timeFilterSelect', sty.select)}
                required
                displayEmpty
                value={selectedTimeFilter}
                onChange={(e) => setSelectedTimeFilter(e.target.value as string)}
                variant="standard"
                // IconComponent={() => }
                MenuProps={{
                    anchorEl: null,
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                    }
                }}
                renderValue={(value) => {
                    if (!value) {
                        return <Body2 color={Colors.grey}>Select time filter</Body2>
                    }
                    return <Body2>{value}</Body2>
                }}
            >
                <MenuItem value="recents">
                    <div className="flex flex-col gap-1">
                        <Body2 color={Colors.black}>Recents</Body2>
                        <Caption color={Colors.greyDark} style={{ width: 300, textWrap: 'wrap' }}>
                            View recently created content
                        </Caption>
                    </div>
                </MenuItem>
                <MenuItem value="futures">
                    <div className="flex flex-col gap-1">
                        <Body2 color={Colors.black}>Futures</Body2>
                        <Caption color={Colors.greyDark} style={{ width: 300, textWrap: 'wrap' }}>
                            View upcoming scheduled content
                        </Caption>
                    </div>
                </MenuItem>
            </Select>
        )
    }

    const handleImportFromUrl = async () => {
        setIsImportingFromUrl(true)
        try {
            const res = await getVideoFromUrl_api(importFromUrlInput)
            if (res) {
                setLocalImportedFromUrlPost(res)
            }
        } catch (error) {
            console.error(error)
            toastError("There was an error importing from your URL. Please try again.")
        } finally {
            setIsImportingFromUrl(false)
        }
    }

    const renderImportFromUrlTab = () => {
        const isAtMaxPosts = MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS;

        return (
            <>
                <InputSymphony
                    className="importFromUrlInputActual"
                    value={importFromUrlInput}
                    onChange={(value) => setImportFromUrlInput(value as string)}
                    placeholder={isAtMaxPosts ? `You can only select ${MAX_POSTS} post${MAX_POSTS && MAX_POSTS > 1 ? 's' : ''}` : "Enter a URL to import from"}
                    onEnter={isAtMaxPosts ? undefined : handleImportFromUrl}
                    isSearch={!isAtMaxPosts}
                    onSearch={isAtMaxPosts ? undefined : handleImportFromUrl}
                    isLoading={isImportingFromUrl}
                    disabled={isImportingFromUrl || isAtMaxPosts}
                    label={isAtMaxPosts ? `Maximum ${MAX_POSTS} post${MAX_POSTS && MAX_POSTS > 1 ? 's' : ''} selected` : "Import a TikTok or Instagram post video from a URL."}
                />
                {localImportedFromUrlPost && !isAtMaxPosts &&
                    <div className="importedFromUrlPostContainer">
                        {renderItemSelector({
                            items: transformPostsToCreativeFormat([localImportedFromUrlPost]),
                            selectedItems: selectedPosts,
                            isLoading: false,
                            className: 'importedFromUrlPostSelector',
                            showLoadMore: false
                        })}
                    </div>
                }
            </>
        )
    }

    /** Helper function to render ItemSelector with consistent styling and behavior */
    const renderItemSelector = ({
        items,
        selectedItems = [],
        isLoading = false,
        className = '',
        showLoadMore = true,
    }: {
        items: CreativeSelectorPost[];
        selectedItems?: CreativeSelectorPost[];
        isLoading?: boolean;
        className?: string;
        showLoadMore?: boolean;
    }) => {
        const selectPostOptions: ItemSelectorAction[] = [
            {
                hideDefaultState: false,
                hideSelectedState: false,
                title: 'Select',
                titleSelected: 'Remove',
                buttonSize: 'small',
                buttonVariant: 'primary',
                buttonVariantSelected: 'outlined',
                onClick: handleSelectPost
            }
        ]

        if (MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS) {
            selectPostOptions[0].hideDefaultState = true
        }

        return (
            <ItemSelector
                className={clsx('itemSelectorPosts', className)}
                items={items}
                selectedItems={selectedItems}
                onSelectItem={handleSelectPost}
                isLoading={isLoading}
                actions={selectPostOptions}
                onLoadMore={showLoadMore ? loadMorePosts : undefined}
                isLoadingMore={isLoadingMorePosts}
            />
        )
    }


    const renderUploader = () => {
        return (
            <Fragment>
                <div className="uploadTabAdCreative">
                    <ButtonSymphony
                        id="upload_button"
                        disabled={MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS}
                        className="uploadButtonAdCreative"
                        style={sty.uploadButton}
                    >
                        {(MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS) ? `You can only select ${MAX_POSTS} post${MAX_POSTS > 1 ? 's' : ''}.` : MAX_POSTS === null ? 'Upload Image or Video' : `Upload Image or Video (${MAX_POSTS} max)`}
                    </ButtonSymphony>
                    <div className="uploadAdCreativeDescription" style={Sty.text14grey}>
                        We recommend uploading at least 1 feed asset (in a 4x5 sizing ratio, like 1080px by 1350px) or 1 story asset (in 9x16 sizing, like 1080px by 1920px).
                    </div>
                </div>
                <CloudinaryUploadWidget
                    buttonId="upload_button"
                    onUpload={handleAssetUpload}
                    maxFiles={MAX_POSTS === null ? undefined : MAX_POSTS - selectedPosts.length}
                />
            </Fragment>
        )
    }
    const renderFacebookContent = () => {
        if (creativeTab.id === 'posts') {
            // Show login banner inside posts tab if user is not logged in
            if (!hasFinishedLogin) {
                return renderLoginBanner();
            }

            const transformedInstagramPosts = transformPostsToCreativeFormat(loadedInstagramPosts);

            return (
                <>
                    {renderItemSelector({
                        items: transformedInstagramPosts,
                        selectedItems: selectedPosts,
                        isLoading: isLoadingInstagramPosts,
                        className: 'facebookPostsSelector',
                        showLoadMore: hasMorePosts
                    })}
                </>
            )
        } else if (creativeTab.id === 'upload') {
            return renderUploader()
        } else if (creativeTab.id === 'url-search') {
            return renderImportMultipleTab()
        }
        return null;
    }

    const renderImportMultipleTab = () => {
        const isAtMaxPosts = MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS;

        const handleImportMultiple = async () => {
            if (!multipleUrlsInput.trim() || isAtMaxPosts) return;

            setIsImportingMultipleUrls(true);

            try {
                const response = await importMultipleInstagramPosts_api({
                    urlString: multipleUrlsInput, // Send raw text as single element array
                    brandId: currentBrand?.id || 0
                });

                if (response) {
                    // Calculate how many posts can be added based on the user's limit
                    const availableSlots = MAX_POSTS === null ? Infinity : MAX_POSTS - selectedPosts.length;

                    // If user has no A/B testing access and already has 1 post, they can't add more
                    if (!canABTest && selectedPosts.length >= 1) {
                        toast.error('You can only select 1 post. Upgrade to PRO to select multiple posts for A/B testing.');
                        return;
                    }

                    // Create a Set of existing post IDs to check for duplicates
                    const existingPostIds = new Set([
                        ...selectedInstagramPosts.map(post => post.id),
                        ...importedFromUrlPosts.map(post => post.id),
                        ...uploadedAssets.map(asset => asset.id)
                    ]);

                    // Filter out duplicates from the response
                    const uniqueInstagramPosts = response.instagramPosts?.filter(post => !existingPostIds.has(post.id)) || [];
                    const uniqueImportedPosts = response.importedPosts?.filter(post => !existingPostIds.has(post.id)) || [];

                    // Limit the posts to add based on available slots
                    const instagramPostsToAdd = uniqueInstagramPosts.slice(0, availableSlots);
                    const remainingSlots = Math.max(0, availableSlots - instagramPostsToAdd.length);
                    const importedPostsToAdd = uniqueImportedPosts.slice(0, remainingSlots);

                    const totalPostsToAdd = instagramPostsToAdd.length + importedPostsToAdd.length;
                    const totalPostsReturned = (response.instagramPosts?.length || 0) + (response.importedPosts?.length || 0);
                    const duplicatesSkipped = totalPostsReturned - (uniqueInstagramPosts.length + uniqueImportedPosts.length);

                    // Handle Instagram posts found in connected accounts
                    if (instagramPostsToAdd.length > 0) {
                        setSelectedInstagramPosts([...selectedInstagramPosts, ...instagramPostsToAdd]);
                    }

                    // Handle posts imported from URLs
                    if (importedPostsToAdd.length > 0) {
                        setImportedFromUrlPosts([...importedFromUrlPosts, ...importedPostsToAdd]);
                    }

                    if (totalPostsToAdd > 0) {
                        // Notify user about successful import
                        let successMessage = `Successfully imported ${totalPostsToAdd} post${totalPostsToAdd !== 1 ? 's' : ''}`;

                        // Add information about duplicates if any were skipped
                        if (duplicatesSkipped > 0) {
                            successMessage += `. ${duplicatesSkipped} duplicate${duplicatesSkipped !== 1 ? 's' : ''} skipped`;
                        }

                        // Add information about posts not added due to limits
                        const notAddedDueToLimit = uniqueInstagramPosts.length + uniqueImportedPosts.length - totalPostsToAdd;
                        if (notAddedDueToLimit > 0) {
                            const limitMessage = MAX_POSTS === null
                                ? `. ${notAddedDueToLimit} post${notAddedDueToLimit !== 1 ? 's' : ''} not added`
                                : `. ${notAddedDueToLimit} post${notAddedDueToLimit !== 1 ? 's' : ''} not added due to your ${MAX_POSTS} post limit`;
                            successMessage += limitMessage;
                        }

                        toast.success(successMessage);
                        setMultipleUrlsInput('');
                    } else if (duplicatesSkipped === totalPostsReturned) {
                        // All posts were duplicates
                        toast.info(`All ${duplicatesSkipped} post${duplicatesSkipped !== 1 ? 's' : ''} were already selected`);
                        setMultipleUrlsInput('');
                    } else if (totalPostsReturned > 0) {
                        // Posts were returned but none could be added (due to limits)
                        const message = canABTest
                            ? `You have already selected the maximum number of posts.`
                            : `You can only select 1 post. ${totalPostsReturned > 1 ? 'Only 1 post can be imported without A/B testing access.' : ''}`;
                        toast.error(message);
                    } else if (response.errors && response.errors.length > 0) {
                        toast.error(response.errors[0]);
                    } else {
                        toast.error('No posts were imported. Please check the URLs and try again.');
                    }
                }
            } catch (error) {
                console.error('Error importing multiple posts:', error);
                toast.error('Failed to import posts. Please try again.');
            } finally {
                setIsImportingMultipleUrls(false);
            }
        };

        return (
            <TabSymphony
                className="importMultipleTabPanel"
                visible={creativeTab.id === 'url-search'}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={Sty.text16grey}>
                        {isAtMaxPosts
                            ? `You can only select ${MAX_POSTS} post${MAX_POSTS && MAX_POSTS > 1 ? 's' : ''}`
                            : 'Paste Instagram or TikTok URLs below (one per line or comma-separated)'
                        }
                    </div>
                    <textarea
                        placeholder={isAtMaxPosts
                            ? `You can only select ${MAX_POSTS} post${MAX_POSTS && MAX_POSTS > 1 ? 's' : ''}`
                            : "https://instagram.com/p/ABC123/\nhttps://instagram.com/p/DEF456/\nhttps://instagram.com/p/GHI789/"
                        }
                        value={multipleUrlsInput}
                        onChange={(e) => setMultipleUrlsInput(e.target.value)}
                        style={{
                            width: '100%',
                            minHeight: 200,
                            padding: 12,
                            borderRadius: 8,
                            border: `1px solid ${Colors.grey}`,
                            fontSize: 14,
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            ...(isAtMaxPosts && {
                                backgroundColor: Colors.greyLight,
                                cursor: 'not-allowed',
                                opacity: 0.6
                            })
                        }}
                        disabled={isImportingMultipleUrls || isAtMaxPosts}
                    />
                    <ButtonSymphony
                        className="importMultipleButton"
                        onClick={handleImportMultiple}
                        disabled={isImportingMultipleUrls || !multipleUrlsInput.trim() || isAtMaxPosts}
                        isLoading={isImportingMultipleUrls}
                        loadingText="Importing posts..."
                        style={{ alignSelf: 'flex-start' }}
                        title={isAtMaxPosts ? `You can only select ${MAX_POSTS} post${MAX_POSTS && MAX_POSTS > 1 ? 's' : ''}` : undefined}
                    >
                        Import
                    </ButtonSymphony>
                </div>
            </TabSymphony>
        );
    };

    const renderSearchPostsModal = () => {
        const renderMaestroLoadingMessage = () => {
            if (!isLoadingSearchPosts) return null;
            return (
                <div className="maestroLoadingMessageContainer">
                    <MaestroLoadingMessage
                        loadingMessages={[
                            'Scanning your Instagram feed...',
                            'Reading through post captions...',
                            'Analyzing engagement metrics...',
                            'Matching search criteria...',
                            'Filtering relevant content...',
                            'Processing media formats...',
                            'Preparing results...']}
                        startLoading={isLoadingSearchPosts}
                    />
                </div>
            )
        }

        const renderInnerModalItems = () => {
            let transformedPosts = transformPostsToCreativeFormat(searchPostsResults)
            let postContent = null

            if (transformedPosts.length > 0) {
                postContent = (
                    <div className="searchPostsResultsContainer">
                        <ItemSelector
                            className="searchPostsResultsSelector"
                            items={transformedPosts}
                            selectedItems={selectedPosts}
                            onSelectItem={handleSelectPost}
                            isLoading={isLoadingSearchPosts}
                            actions={[{
                                hideDefaultState: false,
                                hideSelectedState: false,
                                title: 'Select',
                                titleSelected: 'Remove',
                                buttonSize: 'small',
                                buttonVariant: 'primary',
                                buttonVariantSelected: 'outlined',
                                onClick: handleSelectPost
                            }]}
                        />
                    </div>
                )
            }

            return (
                <>
                    <InputSymphony
                        className="searchPostsInput"
                        placeholder="Search for a post"
                        value={searchPostsInput}
                        isSearch
                        onSearch={handleSearchPosts}
                        onEnter={handleSearchPosts}
                        onChange={(value) => setSearchPostsInput(value as string)}
                    />
                    {renderMaestroLoadingMessage()}
                    {postContent}
                </>
            )

        }

        let buttonRightText = 'Search'
        let buttonRightOnClick = handleSearchPosts
        if (searchPostsResults.length > 0) {
            buttonRightText = 'Save'
            buttonRightOnClick = async () => {
                setSearchPostsInput('')
                setSearchPostsResults([])
            }
        }

        return (
            <ModalSymphony
                className="searchPostsModal"
                isOpen={showSearchSelectorModal}
                title="Search for a post"
                subtitle="Enter an Instagram URL or search with text."
                onClose={handleCloseSearchPostsModal}
                buttonLeftText="Close"
                buttonRightText={buttonRightText}
                buttonLeftVariant="outlined"
                onClickButtonLeft={handleCloseSearchPostsModal}
                onClickButtonRight={buttonRightOnClick}
                buttonLeftDisabled={isLoadingSearchPosts}
                buttonRightDisabled={isLoadingSearchPosts || !searchPostsInput}
                buttonRightIsLoading={isLoadingSearchPosts}
            >
                {renderInnerModalItems()}
            </ModalSymphony>
        )
    }

    const renderSelectorContent = () => {
        if (variant === 'facebook') {
            return renderFacebookContent();
        } else if (variant === 'tiktok') {
            return (
                <>
                    {renderSparkTab()}
                    {renderRecentlyUsedTab()}
                </>
            )
        }

        return null;
    }

    // Render a banner to encourage Facebook login when not logged in
    const renderLoginBanner = () => {
        if (variant !== 'facebook') {
            return null;
        }

        return (
            <Banner
                className="loginFacebookBanner"
                color="purple"
                style={{ marginBottom: 16, gap: 12 }}
                iconLeft={<Icon icon="growInstagramEngagement" className="loginFacebookBannerIcon" />}
                title="Instagram Ads"
            >
                <div className="loginFacebookBannerContent" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p style={{ margin: 0 }}>
                        Login with Facebook to run ads on your Instagram posts, enable Pixel and Conversions Campaigns, and grow followers
                    </p>
                    <div className="loginFacebookBannerButtons" style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                        <ButtonSymphony
                            className="loginFacebookButton"
                            onClick={() => setShowConnectionsModal(true)}
                            variant="primary"
                            isLoading={isLoggingIntoFacebook}
                        >
                            Login with Facebook
                        </ButtonSymphony>
                        {!requireFBLogin &&
                            <ButtonSymphony
                                className="loginFacebookButton"
                                onClick={() => {
                                    setShowConnectionsModal(false)
                                    // Switch to upload tab when user clicks Skip for now
                                    const uploadTab = creativeTabs.find(tab => tab.id === 'upload');
                                    if (uploadTab) {
                                        setCreativeTab(uploadTab);
                                    }
                                }}
                                variant="text"
                            >
                                Skip for now
                            </ButtonSymphony>
                        }
                    </div>
                </div>
            </Banner>
        );
    };


    let subtitle = "Choose a post or reel for your ad, or upload a custom video."
    if (variant === 'tiktok') {
        subtitle = "Create a TikTok Ad Code, commonly called a Spark Code, to boost your post through Symphony."
    }

    let innerContent;
    if (playbookType === PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT && (!loggedInFacebookUser || !selectedFacebookAdAccount)) {
        innerContent = renderFacebookContent()
    } else {
        innerContent = (
            <>
                <TabsSymphony
                    className="adCreativeSelectorCampaignTabs"
                    tabs={creativeTabs}
                    selectedTab={creativeTab!}
                    onChange={(tabId) => setCreativeTab(tabId as TabItem)}
                    isBorderBottom
                    isLoading={isLoadingInstagramPosts}
                    rightSideContent={canUsePlatformPosts ?
                        <div style={{ display: 'flex', gap: 8 }}>
                            <ButtonSymphony
                                className="searchForPosts"
                                variant="text"
                                onClick={() => setShowSearchSelectorModal(true)}
                                disabled={MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS}
                                title={MAX_POSTS !== null && selectedPosts.length >= MAX_POSTS ? `You can only select ${MAX_POSTS} post${MAX_POSTS > 1 ? 's' : ''}` : undefined}
                            >
                                Search
                            </ButtonSymphony>
                        </div>
                        :
                        null
                    }
                />

                {renderSelectorContent()}
                {renderSelectedPosts()}
                {renderSearchPostsModal()}
            </>
        )
    }

    return (
        <>
            <DebugBox
                data={{
                    variant,
                    selectedSparkCodes,
                    selectedPosts,
                }}
            />
            <CardSymphony
                className={clsx("adCreativeSelectorCampaignComponent", className)}
                style={style}
                title={hideTitle ? undefined : "Ad Creative"}
                subtitle={hideSubtitle ? undefined : subtitle}
                size="small"
                isPadding16
            >
                {innerContent}
            </CardSymphony>
        </>
    );
}

const sty: Record<string, CSSProperties> = {
    tabsAndFilterContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16
    },
    select: {
        minWidth: 150,
        // '& .MuiSelect-select': {
        //     paddingRight: 24
        // }
    },
    postSelectedNumber: {
        position: 'absolute',
        top: 4,
        right: 4,
        backgroundColor: Colors.purple,
        color: Colors.white,
        borderRadius: 999,
        width: 24,
        height: 24,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    selectedSparkCodesContainer: {
        marginTop: 24,
    },
    sparkCodesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 8,
    },
    sparkCodeItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 8,
        backgroundColor: Colors.greyLight,
        borderRadius: 4,
    },
    sparkCodeText: {
        ...Sty.text14,
        flex: 1,
    },
    creativeTabAdSelector: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    selectedPostsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    selectedSparkPostContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 12,
        paddingRight: 16,
        border: Sty.border,
        borderRadius: 8,
    },
    selectedSparkPostImage: {
        width: 40,
        height: 40,
        objectFit: 'cover',
        borderRadius: 8,
    },
    selectedPostsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    uploadButton: {
        borderStyle: 'dashed',
        backgroundColor: Colors.purpleTransparent,
        borderWidth: 1,
        borderColor: Colors.purple,
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
        marginTop: 16,
        color: Colors.purple,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        height: 72
    },
    loadMoreButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 16,
        marginBottom: 8,
    }
}

export default AdCreativeSelectorCampaign;